/**
 * Maintenance and repair (ADR-63 Cut 4, design section 25). A maintenance event
 * is a problem with an asset or a kit and what was done about it: an inspection
 * finding, a repair, a failure, a battery swap, a cleaning. It has a small
 * lifecycle so "what is in the shop right now" is a query, not a guess.
 *
 * An OPEN or IN_PROGRESS event is UNRESOLVED, and an unresolved event on a kit
 * (or one of its member assets) makes the kit read NEEDS_MAINTENANCE even when
 * every part is present: readiness is derived, and this is one more input to it.
 */

export const MAINTENANCE_EVENT_TYPES = [
  'INSPECTION',
  'PREVENTIVE_MAINTENANCE',
  'REPAIR',
  'DAMAGE',
  'FAILURE',
  'BATTERY_REPLACEMENT',
  'FIRMWARE_UPDATE',
  'SOFTWARE_UPDATE',
  'CLEANING',
  'REPLACEMENT',
  'RETIREMENT',
  'OTHER',
] as const;
export type MaintenanceEventType = (typeof MAINTENANCE_EVENT_TYPES)[number];

export const MAINTENANCE_EVENT_TYPE_LABEL: Readonly<Record<MaintenanceEventType, string>> = {
  INSPECTION: 'Inspection',
  PREVENTIVE_MAINTENANCE: 'Preventive maintenance',
  REPAIR: 'Repair',
  DAMAGE: 'Damage',
  FAILURE: 'Failure',
  BATTERY_REPLACEMENT: 'Battery replacement',
  FIRMWARE_UPDATE: 'Firmware update',
  SOFTWARE_UPDATE: 'Software update',
  CLEANING: 'Cleaning',
  REPLACEMENT: 'Replacement',
  RETIREMENT: 'Retirement',
  OTHER: 'Other',
};

export const MAINTENANCE_STATUSES = ['OPEN', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'] as const;
export type MaintenanceStatus = (typeof MAINTENANCE_STATUSES)[number];

export const MAINTENANCE_STATUS_LABEL: Readonly<Record<MaintenanceStatus, string>> = {
  OPEN: 'Open',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

/** Unresolved: something is still wrong or being worked on. */
export const MAINTENANCE_OPEN_STATUSES: readonly MaintenanceStatus[] = ['OPEN', 'IN_PROGRESS'];

export function isMaintenanceOpen(status: MaintenanceStatus): boolean {
  return MAINTENANCE_OPEN_STATUSES.includes(status);
}

/** The lifecycle. COMPLETED and CANCELLED are terminal; a reopen is a new event. */
export const MAINTENANCE_TRANSITIONS: Readonly<Record<MaintenanceStatus, readonly MaintenanceStatus[]>> = {
  OPEN: ['IN_PROGRESS', 'COMPLETED', 'CANCELLED'],
  IN_PROGRESS: ['COMPLETED', 'CANCELLED', 'OPEN'],
  COMPLETED: [],
  CANCELLED: [],
};

export function canTransitionMaintenance(from: MaintenanceStatus, to: MaintenanceStatus): boolean {
  return MAINTENANCE_TRANSITIONS[from].includes(to);
}

export function isMaintenanceEventType(v: unknown): v is MaintenanceEventType {
  return typeof v === 'string' && (MAINTENANCE_EVENT_TYPES as readonly string[]).includes(v);
}
